import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  IconButton,
  Grid,
  Dialog,
  Typography,
  TextField,
  Button,
  CircularProgress,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import PropTypes from 'prop-types';
import toaster from 'react-hot-toast';
import { ThemePreferencesControls } from 'components/common/themePreferencesControls';
import { LIVELINK_ORDER_PAGE } from 'utils/config';
import {
  THEME_VALUES,
  BUTTONS,
  THEME_ELEMENTS,
  THEME_TYPES,
  THEME_MESSAGES,
} from 'utils/constants';
import { saveThemeChanges } from 'utils/themes/saveThemeChanges';
import { ThemeList } from '../themeList';

export const CertainThemeEditor = ({
  initialEditableCertainTheme,
  setInitialEditableCertainTheme,
  refreshStoreAndBusiness,
  businessId,
  businessThemeLink,
  setRequestResponse,
  setRequestError,
}) => {
  const [isCommonThemesListOpened, setIsCommonThemesListOpened] = useState(false);
  const [commonThemeToApply, setCommonThemeToApply] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  const [logo, setLogo] = useState('');
  const [name, setName] = useState('');
  const handleNameChange = (event) => {
    setName(event.target.value);
  };
  const [borderRadius, setBorderRadius] = useState(THEME_VALUES?.roundedRadius);
  const [primaryColor, setPrimaryColor] = useState(THEME_VALUES.defaultPrimaryColor);
  const [url, setUrl] = useState('');
  const handleUrlChange = (event) => {
    setUrl(event.target.value);
  };

  const type = initialEditableCertainTheme?.storeId ? THEME_TYPES.store : THEME_TYPES.business;
  const isStoreTheme = type === THEME_TYPES.store;

  const setFormState = (theme) => {
    const existingUrl = theme?.customUrl || theme?.encodedLink;
    theme?.name && setName(theme.name);
    existingUrl && setUrl(existingUrl);
    setLogo(theme.logoUrl);
    setBorderRadius(theme.borderRadius);
    setPrimaryColor(theme.primaryColor);
  };

  useEffect(() => {
    if (commonThemeToApply) {
      setFormState(commonThemeToApply);
      setCommonThemeToApply(null);
    }
  }, [commonThemeToApply]);

  useEffect(() => {
    if (initialEditableCertainTheme) {
      setFormState(initialEditableCertainTheme);
    }
  }, [initialEditableCertainTheme]);

  const closeEditor = () => {
    setName('');
    setUrl('');
    setBorderRadius('');
    setPrimaryColor('');
    setLogo('');
    setActionInProgress(false);
    setInitialEditableCertainTheme(null);
    setIsCommonThemesListOpened(false);
    setCommonThemeToApply(null);
  };

  const saveChanges = async () => {
    const newTheme = {
      borderRadius,
      primaryColor,
      logoUrl: logo,
      storeId: initialEditableCertainTheme.storeId,
    };
    try {
      setActionInProgress(true);
      await saveThemeChanges({
        initialTheme: initialEditableCertainTheme,
        newTheme,
        url,
        name,
      });
      setRequestResponse(
        `${initialEditableCertainTheme?.name || THEME_ELEMENTS.businessLabel} was updated!`,
      );
      closeEditor();
      await refreshStoreAndBusiness();
    } catch (error) {
      setActionInProgress(false);
      setRequestError(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
    }
  };

  const copyLiveLinkUrl = () => {
    try {
      let liveLinkUrl = `${LIVELINK_ORDER_PAGE}${businessThemeLink}`;
      if (isStoreTheme) liveLinkUrl += `/${url}`;

      if ('clipboard' in navigator) {
        navigator.clipboard.writeText(liveLinkUrl);
      } else {
        document.execCommand('copy', true, liveLinkUrl); // for browsers that don't support clipboard API
      }
      toaster('Live Link URL copied!', {
        icon: '✅',
      });
    } catch {
      toaster.error('Failed to copy Live Link URL. Please try again.');
    }
  };

  return (
    <>
      <Dialog
        className="certain-theme-editor"
        onClose={closeEditor}
        open={Boolean(initialEditableCertainTheme)}
        maxWidth="lg"
      >
        <DialogTitle disableTypography>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{`Edit ${type} theme:`}</Typography>
            <IconButton aria-label="close" onClick={closeEditor}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          {isStoreTheme ? (
            <TextField
              className="theme-input"
              label={THEME_ELEMENTS.name}
              value={name}
              onChange={handleNameChange}
              autoFocus
            />
          ) : null}
          <TextField
            className="theme-input"
            label={THEME_ELEMENTS.url}
            value={url}
            onChange={handleUrlChange}
          />
          {url && (
            <Grid classes={{ root: 'controls-container' }}>
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  copyLiveLinkUrl();
                }}
                color="primary"
                size="small"
                variant="contained"
                classes={{ root: 'store-theme-cell-button' }}
              >
                {BUTTONS.copyLiveLinkUrl}
              </Button>
            </Grid>
          )}

          <ThemePreferencesControls
            logo={logo}
            setLogo={setLogo}
            primaryColor={primaryColor}
            setPrimaryColor={setPrimaryColor}
            borderRadius={borderRadius}
            setBorderRadius={setBorderRadius}
            type={type}
            setIsCommonThemesListOpened={setIsCommonThemesListOpened}
          />
        </DialogContent>
        <ThemeList
          setThemeFromList={setCommonThemeToApply}
          isThemeListOpened={isCommonThemesListOpened}
          setIsThemeListOpened={setIsCommonThemesListOpened}
          businessId={businessId}
          refreshStoreAndBusiness={refreshStoreAndBusiness}
          setRequestResponse={setRequestResponse}
          setRequestError={setRequestError}
        />
        <Grid classes={{ root: 'controls-container' }}>
          <Button
            color="primary"
            variant="contained"
            onClick={saveChanges}
            startIcon={actionInProgress ? <CircularProgress color="inherit" size={20} /> : null}
          >
            {BUTTONS.save}
          </Button>
          <Button color="secondary" variant="contained" onClick={closeEditor}>
            {BUTTONS.cancel}
          </Button>
        </Grid>
      </Dialog>
    </>
  );
};

CertainThemeEditor.propTypes = {
  initialEditableCertainTheme: PropTypes.object,
  setInitialEditableCertainTheme: PropTypes.func.isRequired,
  refreshStoreAndBusiness: PropTypes.func.isRequired,
  businessId: PropTypes.string,
  setRequestResponse: PropTypes.func.isRequired,
  setRequestError: PropTypes.func.isRequired,
};
